<script setup>
import { useI18n } from 'vue-i18n';
import RealEstateCommissionSheetEditor from './RealEstateCommissionSheetEditor.vue';
import RealEstateCommissionSheetPreview from './RealEstateCommissionSheetPreview.vue';
import RealEstateCommissionSheetTotals from './RealEstateCommissionSheetTotals.vue';

defineProps({
  activeGroup: { type: Object, required: true },
  activeGroupEnabled: { type: Boolean, default: true },
  activeGroupError: { type: String, default: '' },
  activeGroupQuantity: { type: Number, default: 0 },
  amountCents: { type: Number, default: 0 },
  canEdit: { type: Boolean, default: false },
  menuItems: { type: Array, default: () => [] },
  optionistReport: { type: Object, default: null },
  optionistReportExceeds: { type: Boolean, default: false },
  optionistReportTypes: { type: Array, default: () => [] },
  preview: { type: Boolean, default: false },
  sheetLabel: { type: String, required: true },
  state: { type: Object, required: true },
  title: { type: String, required: true },
});

const emit = defineEmits([
  'add',
  'payment',
  'remove',
  'select-group',
  'select-recipient',
  'update',
  'update-enabled',
  'update-optionist-report',
  'update-quantity',
]);
const { t } = useI18n();
</script>

<template>
  <main
    class="mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-4 pb-28 sm:px-6 lg:px-10"
  >
    <div
      v-if="state.loading.value"
      class="rounded-lais border border-lais-border-soft bg-white p-10 text-center text-sm text-lais-text-muted"
    >
      {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.COMMON.LOADING') }}
    </div>
    <p
      v-else-if="state.error.value"
      class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      {{ state.error.value }}
    </p>
    <template v-else-if="state.installment.value">
      <p
        v-if="state.installment.value.enviada"
        class="rounded-lais border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
      >
        {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.EDITOR.READ_ONLY') }}
      </p>
      <RealEstateCommissionSheetPreview
        v-if="preview"
        :amount-cents="amountCents"
        :form="state.form.value"
        :installment="state.installment.value"
        :totals="state.totals.value"
      />
      <RealEstateCommissionSheetEditor
        v-else
        :active-group="activeGroup"
        :active-group-enabled="activeGroupEnabled"
        :active-group-error="activeGroupError"
        :active-group-quantity="activeGroupQuantity"
        :amount-cents="amountCents"
        :can-edit="canEdit"
        :form="state.form.value"
        :menu-items="menuItems"
        :optionist-report="optionistReport"
        :optionist-report-exceeds="optionistReportExceeds"
        :optionist-report-types="optionistReportTypes"
        :sheet-label="sheetLabel"
        :title="title"
        @add="emit('add')"
        @payment="emit('payment', $event)"
        @remove="emit('remove', $event)"
        @select-group="emit('select-group', $event)"
        @select-recipient="
          (index, optionId) => emit('select-recipient', index, optionId)
        "
        @update="(index, field, value) => emit('update', index, field, value)"
        @update-enabled="emit('update-enabled', $event)"
        @update-optionist-report="emit('update-optionist-report', $event)"
        @update-quantity="emit('update-quantity', $event)"
      />
      <RealEstateCommissionSheetTotals
        :amount-cents="amountCents"
        :totals="state.totals.value"
      />
    </template>
  </main>
</template>
